import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TripleFormDialog = ({ open, subjectId, relations, headers, onCancel, onSubmit }) => {
  const [relationId, setRelationId] = useState('');
  const [objectId, setObjectId] = useState('');
  const [description, setDescription] = useState('');
  const [candidates, setCandidates] = useState([]);

  const selectedRel = relations.find(r => r.relation_id === relationId);

  useEffect(() => {
    if (!selectedRel) { setCandidates([]); return; }
    (async () => {
      try {
        const rangeClasses = selectedRel.range_class_ids || [];
        const params = {};
        if (rangeClasses.length === 1) params.class_id = rangeClasses[0];
        const r = await axios.get(`${API}/ontology/individuals`, { params, headers });
        let list = r.data || [];
        if (rangeClasses.length > 1) list = list.filter(i => rangeClasses.includes(i.class_id));
        setCandidates(list.filter(i => i.individual_id !== subjectId));
      } catch { /* */ }
    })();
  }, [selectedRel, subjectId, headers]);

  const canSubmit = relationId && objectId;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add Triple (relation assertion)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Relation *</Label>
            <Select value={relationId} onValueChange={setRelationId}>
              <SelectTrigger data-testid="triple-relation-select"><SelectValue placeholder="Select a relation" /></SelectTrigger>
              <SelectContent>
                {relations.length === 0 && <SelectItem value="none" disabled>No relations defined</SelectItem>}
                {relations.map(r => <SelectItem key={r.relation_id} value={r.relation_id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Object individual *</Label>
            <Select value={objectId} onValueChange={setObjectId} disabled={!relationId}>
              <SelectTrigger data-testid="triple-object-select"><SelectValue placeholder={relationId ? 'Select an individual' : 'Pick a relation first'} /></SelectTrigger>
              <SelectContent>
                {candidates.length === 0 && <SelectItem value="none" disabled>No valid candidates</SelectItem>}
                {candidates.map(c => <SelectItem key={c.individual_id} value={c.individual_id}>{c.name} ({c.class_name})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button className="bg-[#002FA7] hover:bg-[#001F6F]" disabled={!canSubmit}
            onClick={() => onSubmit({ subject_individual_id: subjectId, relation_id: relationId, object_individual_id: objectId, description })}
            data-testid="submit-triple">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TripleFormDialog;
